import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { MobileMenuProvider } from "@/context/MobileMenuContext";
import { RateProvider } from "@/context/RateContext";
import { PdfViewerProvider } from "@/context/PdfViewerContext";
import PrivateLayout from "@/components/PrivateLayout";
import LoginLayout from "@/components/LoginLayout";
import Login from "@/pages/Login";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import PWAUpdateToast from "@/components/PWAUpdateToast";

// Lazy load page components
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const History = lazy(() => import("@/pages/History"));
const Profile = lazy(() => import("@/pages/Profile"));
const Wallet = lazy(() => import("@/pages/Wallet"));
const Receipts = lazy(() => import("@/pages/Receipts"));
const Funds = lazy(() => import("@/pages/Funds"));
const WithdrawRequest = lazy(() => import("@/pages/WithdrawRequest"));
const DepositReceiptNew = lazy(() => import("@/pages/DepositReceiptNew"));
const DepositsPending = lazy(() => import("@/pages/DepositsPending"));
const WithdrawalsPending = lazy(() => import("@/pages/WithdrawalsPending"));
const PendingReceipts = lazy(() => import("@/pages/PendingReceipts"));
const DepositReceiptsArchive = lazy(() => import("@/pages/DepositReceiptsArchive"));
const Transfers = lazy(() => import("@/pages/Transfers"));
const AutoOrders = lazy(() => import("@/pages/AutoOrders"));
const SetPassword = lazy(() => import("@/pages/SetPassword"));
const PendingApproval = lazy(() => import("@/pages/PendingApproval"));
const ChangePassword = lazy(() => import("@/pages/ChangePassword"));
const TraderPrices = lazy(() => import("@/pages/TraderPrices"));
const AccountingReportPdfSample = lazy(() => import("@/pages/AccountingReportPdfSample"));

// Loading component
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center space-y-3">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="text-sm text-muted-foreground">در حال بارگذاری...</p>
      </div>
    </div>
  );
}

function PrivateRoute({ children, allowBlocked = false }) {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.status === 'blocked' && !allowBlocked) {
    return <Navigate to="/pending-approval" replace />;
  }
  return <PrivateLayout>{children}</PrivateLayout>;
}

function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <LoginLayout>{children}</LoginLayout>;
}

function RouteWrapper({ children }) {
  const location = useLocation();
  const refreshKey = location.state?.refresh || 0;
  return <div className="route-wrapper bg-white md:m-0 md:mt-4 md:rounded-lg" key={refreshKey}>{children}</div>;
}

export default function App() {
  return (
    <AuthProvider>
      <PdfViewerProvider>
        <RateProvider>
          <MobileMenuProvider>
            <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                  <Route path="/set-password" element={<PrivateRoute allowBlocked={true}><SetPassword /></PrivateRoute>} />
                  <Route path="/pending-approval" element={<PrivateRoute allowBlocked={true}><PendingApproval /></PrivateRoute>} />
                  <Route path="/" element={<PrivateRoute><RouteWrapper><Dashboard /></RouteWrapper></PrivateRoute>} />
                  <Route path="/history" element={<PrivateRoute><RouteWrapper><History /></RouteWrapper></PrivateRoute>} />
                  <Route path="/wallet" element={<PrivateRoute><RouteWrapper><Wallet /></RouteWrapper></PrivateRoute>} />
                  <Route path="/profile" element={<PrivateRoute><RouteWrapper><Profile /></RouteWrapper></PrivateRoute>} />
                  <Route path="/change-password" element={<PrivateRoute><RouteWrapper><ChangePassword /></RouteWrapper></PrivateRoute>} />
                  <Route path="/receipts" element={<PrivateRoute><RouteWrapper><Receipts /></RouteWrapper></PrivateRoute>} />
                  <Route path="/funds" element={<PrivateRoute><RouteWrapper><Funds /></RouteWrapper></PrivateRoute>} />
                  <Route path="/withdraw/request" element={<PrivateRoute><RouteWrapper><WithdrawRequest /></RouteWrapper></PrivateRoute>} />
                  <Route path="/deposit/receipt/new" element={<PrivateRoute><RouteWrapper><DepositReceiptNew /></RouteWrapper></PrivateRoute>} />
                  <Route path="/deposits/pending" element={<PrivateRoute><RouteWrapper><DepositsPending /></RouteWrapper></PrivateRoute>} />
                  <Route path="/withdrawals/pending" element={<PrivateRoute><RouteWrapper><WithdrawalsPending /></RouteWrapper></PrivateRoute>} />
                  <Route path="/receipts/pending" element={<PrivateRoute><RouteWrapper><PendingReceipts /></RouteWrapper></PrivateRoute>} />
                  <Route path="/deposit-receipts/archive" element={<PrivateRoute><RouteWrapper><DepositReceiptsArchive /></RouteWrapper></PrivateRoute>} />
                  <Route path="/transfers" element={<PrivateRoute><RouteWrapper><Transfers /></RouteWrapper></PrivateRoute>} />
                  <Route path="/auto-orders" element={<PrivateRoute><RouteWrapper><AutoOrders /></RouteWrapper></PrivateRoute>} />
                  <Route path="/trader-prices" element={<PrivateRoute><RouteWrapper><TraderPrices /></RouteWrapper></PrivateRoute>} />
                  <Route
                    path="/accounting-report-sample"
                    element={<PrivateRoute><RouteWrapper><AccountingReportPdfSample /></RouteWrapper></PrivateRoute>}
                  />
                  <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
              </Suspense>
              <PWAInstallPrompt />
            </BrowserRouter>
            <PWAUpdateToast />
          </MobileMenuProvider>
        </RateProvider>
      </PdfViewerProvider>
    </AuthProvider>
  );
}
